import React, { useEffect, useMemo, useState } from 'react';
import { Box, Divider, Stack, styled, Typography } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { HubPatient } from 'types';
import {
  AndroidIcon,
  AppleIcon,
  ChromeIcon,
  FirefoxIcon,
  LaptopIcon,
  MicIcon,
  MicOffIcon,
  MobilePhoneIcon,
  SafariIcon,
  TabletIcon,
  VideoCameraIcon,
  VideoCameraOffIcon,
  WindowsIcon,
} from 'assets';
import { checkBrowserVersion, checkOsVersion } from 'utils';

import styles from './PatientStatus.module.scss';

export interface PatientStatusProps {
  patient: HubPatient;
}

const VideoCameraIconStyled = styled(VideoCameraIcon)(({ theme }) => ({
  color: '#3EB157',
}));

const VideoCameraOffIconStyled = styled(VideoCameraOffIcon)(({ theme }) => ({
  color: '#FF3C3C',
}));

const MicIconStyled = styled(MicIcon)(({ theme }) => ({
  color: '#3EB157',
}));

const MicOffIconStyled = styled(MicOffIcon)(({ theme }) => ({
  color: '#FF3C3C',
}));

const LaptopIconStyled = styled(LaptopIcon)(({ theme }) => ({
  color: theme.palette.text.primary,
}));

const TabletIconStyled = styled(TabletIcon)(({ theme }) => ({
  color: theme.palette.text.primary,
}));

const MobilePhoneIconStyled = styled(MobilePhoneIcon)(({ theme }) => ({
  color: theme.palette.text.primary,
}));

const WindowsIconStyled = styled(WindowsIcon)(({ theme }) => ({
  color: theme.palette.text.primary,
}));

export function PatientStatus({ patient }: PatientStatusProps) {
  const { t, i18n } = useTranslation('providerRoom');
  const isRtl = i18n.dir() === 'rtl';
  const [waitingTime, setWaitingTime] = useState(0);
  const statusRowSx = {
    width: '100%',
    direction: 'ltr' as const,
    justifyContent: isRtl ? ('flex-end' as const) : ('flex-start' as const),
    gap: '10px',
  };
  const statusTextSx = { textAlign: isRtl ? 'right' : ('left' as const) };

  const localizeDeviceType = (value?: string) => {
    if (!value?.trim()) return t('statusUnknown');

    const trimmed = value.trim();
    const lower = trimmed.toLowerCase();

    if (lower === 'laptop/desktop' || lower.includes('desktop') || lower.includes('laptop')) {
      return t('statusDeviceLaptopDesktop');
    }

    if (lower.startsWith('tablet')) {
      return trimmed.replace(/^tablet/i, t('statusDeviceTablet'));
    }

    if (lower.startsWith('mobile') || lower.startsWith('phone')) {
      return trimmed.replace(/^(mobile|phone)/i, t('statusDeviceMobile'));
    }

    return trimmed;
  };

  const localizeOperatingSystem = (value?: string) => {
    if (!value?.trim()) return t('statusUnknown');

    const trimmed = value.trim();
    const lower = trimmed.toLowerCase();

    if (lower.startsWith('windows')) {
      return trimmed.replace(/^windows/i, t('statusOsWindows'));
    }

    if (lower.startsWith('android')) {
      return trimmed.replace(/^android/i, t('statusOsAndroid'));
    }

    if (lower.startsWith('mac os') || lower.startsWith('macos') || lower.startsWith('mac')) {
      return trimmed.replace(/^mac(\s?os)?/i, t('statusOsMac'));
    }

    if (lower.startsWith('ios')) {
      return trimmed.replace(/^ios/i, t('statusOsIos'));
    }

    return trimmed;
  };

  const localizeBrowser = (value?: string) => {
    if (!value?.trim()) return t('statusUnknown');

    const trimmed = value.trim();
    const lower = trimmed.toLowerCase();

    if (lower.startsWith('mozilla firefox')) {
      return trimmed.replace(/^mozilla firefox/i, t('statusBrowserFirefox'));
    }

    if (lower.startsWith('firefox')) {
      return trimmed.replace(/^firefox/i, t('statusBrowserFirefox'));
    }

    if (lower.startsWith('google chrome')) {
      return trimmed.replace(/^google chrome/i, t('statusBrowserChrome'));
    }

    if (lower.startsWith('chrome')) {
      return trimmed.replace(/^chrome/i, t('statusBrowserChrome'));
    }

    if (lower.startsWith('safari')) {
      return trimmed.replace(/^safari/i, t('statusBrowserSafari'));
    }

    if (lower.startsWith('edge')) {
      return trimmed.replace(/^edge/i, t('statusBrowserEdge'));
    }

    if (lower.startsWith('opera')) {
      return trimmed.replace(/^opera/i, t('statusBrowserOpera'));
    }

    return trimmed;
  };

  useEffect(() => {
    const updateWaitingTime = () => {
      const now = moment();
      const joined = moment(patient.joinedOn, 'YYYY-MM-DDTHH:mm:ss.SSSSZ');
      const minutes = Math.max(0, Math.floor(moment.duration(now.diff(joined)).asMinutes()));
      setWaitingTime(minutes);
    };

    updateWaitingTime();
    const timer = setInterval(updateWaitingTime, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [patient.joinedOn]);

  const deviceIcon = useMemo(() => {
    const deviceType = patient.device?.type?.toLowerCase();
    let icon = null;
    if (deviceType) {
      switch (true) {
        case deviceType.indexOf('tablet') !== -1: {
          icon = <TabletIconStyled />;
          break;
        }
        case deviceType.indexOf('mobile') !== -1: {
          icon = <MobilePhoneIconStyled />;
          break;
        }
        default: {
          icon = <LaptopIconStyled />;
          break;
        }
      }
    }
    return icon;
  }, [patient.device]);

  const operationSystemIcon = useMemo(() => {
    const operationSystem = patient.device?.operationSystem?.toLowerCase();
    let icon = null;
    if (operationSystem) {
      switch (true) {
        case operationSystem.indexOf('android') !== -1: {
          icon = <AndroidIcon />;
          break;
        }
        case operationSystem.indexOf('windows') !== -1: {
          icon = <WindowsIconStyled />;
          break;
        }
        default: {
          icon = <AppleIcon />;
          break;
        }
      }
    }
    return icon;
  }, [patient.device]);

  const browserIcon = useMemo(() => {
    const operationSystem = patient.device?.browser?.toLowerCase();
    let icon = null;
    if (operationSystem) {
      switch (true) {
        case operationSystem.indexOf('mozilla') !== -1:
        case operationSystem.indexOf('firefox') !== -1: {
          icon = <FirefoxIcon />;
          break;
        }
        case operationSystem.indexOf('safari') !== -1: {
          icon = <SafariIcon />;
          break;
        }
        default: {
          icon = <ChromeIcon />;
          break;
        }
      }
    }
    return icon;
  }, [patient.device]);

  const browserCheck = useMemo(() => checkBrowserVersion(patient.device?.browser), [patient.device]);
  const osCheck = useMemo(() => checkOsVersion(patient.device?.operationSystem), [patient.device]);

  return (
    <Box className={styles.container}>
      <Stack direction='column' spacing={'5px'} alignItems={isRtl ? 'flex-end' : 'flex-start'} sx={{ width: '100%' }}>
        <Typography component='p' variant='body2' sx={{ textAlign: isRtl ? 'right' : 'left', width: '100%' }}>
          {patient.userName}
        </Typography>

        <Stack direction='row' alignItems='center' justifyContent='flex-start' sx={statusRowSx}>
          <Typography
            component='p'
            variant='body4'
            style={{ color: waitingTime < 5 ? '#3EB157' : '#FF3C3C' }}
            sx={statusTextSx}
          >
            {t('waitingMinutes', { count: waitingTime })}
          </Typography>
          {patient.callAccesses?.hasMicroAccess ? (
            <MicIconStyled className={styles.micIcon} />
          ) : (
            <MicOffIconStyled className={styles.micIcon} />
          )}
          {patient.callAccesses?.hasCameraAccess ? (
            <VideoCameraIconStyled className={styles.videoIcon} />
          ) : (
            <VideoCameraOffIconStyled className={styles.videoIcon} />
          )}
        </Stack>
        {!patient.callAccesses?.hasMicroAccess && patient.callAccesses?.microAccessError && (
          <Stack direction='row' alignItems='center' sx={statusRowSx}>
            <MicOffIconStyled sx={{ fontSize: '14px' }} />
            <Typography component='p' variant='caption' sx={{ color: '#FF3C3C', fontSize: '0.65rem', ml: 0 }}>
              {t(`accessError_${patient.callAccesses.microAccessError}` as any, {
                defaultValue: t('accessErrorUnknown'),
              })}
            </Typography>
          </Stack>
        )}
        {!patient.callAccesses?.hasCameraAccess && patient.callAccesses?.cameraAccessError && (
          <Stack direction='row' alignItems='center' sx={{ ...statusRowSx, mt: '0 !important' }}>
            <VideoCameraOffIconStyled sx={{ fontSize: '14px' }} />
            <Typography component='p' variant='caption' sx={{ color: '#FF3C3C', fontSize: '0.65rem', ml: 0 }}>
              {t(`accessError_${patient.callAccesses.cameraAccessError}` as any, {
                defaultValue: t('accessErrorUnknown'),
              })}
            </Typography>
          </Stack>
        )}
      </Stack>
      <Divider />
      {patient.device && (
        <>
          <Stack direction='row' alignItems='center' justifyContent='flex-start' sx={statusRowSx}>
            {isRtl ? (
              <>
                <Typography component='p' variant='body2' sx={statusTextSx}>
                  {localizeDeviceType(patient.device.type)}
                </Typography>
                {deviceIcon}
              </>
            ) : (
              <>
                {deviceIcon}
                <Typography component='p' variant='body2' sx={statusTextSx}>
                  {localizeDeviceType(patient.device.type)}
                </Typography>
              </>
            )}
          </Stack>
          <Stack direction='row' alignItems='center' justifyContent='flex-start' sx={statusRowSx}>
            {isRtl ? (
              <>
                <Box sx={statusTextSx}>
                  <Typography component='p' variant='body2' sx={{ color: osCheck.ok ? 'text.primary' : '#FF3C3C' }}>
                    {localizeOperatingSystem(patient.device.operationSystem)}
                  </Typography>
                  {!osCheck.ok && (
                    <Typography component='p' variant='body1' className={styles.updateHint}>
                      {t('statusUpdateTo', { version: osCheck.minLabel })}
                    </Typography>
                  )}
                </Box>
                {operationSystemIcon}
              </>
            ) : (
              <>
                {operationSystemIcon}
                <Box sx={statusTextSx}>
                  <Typography component='p' variant='body2' sx={{ color: osCheck.ok ? 'text.primary' : '#FF3C3C' }}>
                    {localizeOperatingSystem(patient.device.operationSystem)}
                  </Typography>
                  {!osCheck.ok && (
                    <Typography component='p' variant='body1' className={styles.updateHint}>
                      {t('statusUpdateTo', { version: osCheck.minLabel })}
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Stack>
          <Stack direction='row' alignItems='center' justifyContent='flex-start' sx={statusRowSx}>
            {isRtl ? (
              <>
                <Box sx={statusTextSx}>
                  <Typography
                    component='p'
                    variant='body2'
                    sx={{ color: browserCheck.ok ? 'text.primary' : '#FF3C3C' }}
                  >
                    {localizeBrowser(patient.device.browser)}
                  </Typography>
                  {!browserCheck.ok && (
                    <Typography component='p' variant='body1' className={styles.updateHint}>
                      {t('statusUpdateTo', { version: browserCheck.minLabel })}
                    </Typography>
                  )}
                </Box>
                {browserIcon}
              </>
            ) : (
              <>
                {browserIcon}
                <Box sx={statusTextSx}>
                  <Typography
                    component='p'
                    variant='body2'
                    sx={{ color: browserCheck.ok ? 'text.primary' : '#FF3C3C' }}
                  >
                    {localizeBrowser(patient.device.browser)}
                  </Typography>
                  {!browserCheck.ok && (
                    <Typography component='p' variant='body1' className={styles.updateHint}>
                      {t('statusUpdateTo', { version: browserCheck.minLabel })}
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Stack>
        </>
      )}
    </Box>
  );
}
